// src/components/Admin/tabs/RoomsTab.jsx
// Cuartos del hotel: crear, editar (con hasta 3 fotos), ocultar y eliminar.
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createRoomAdmin, updateRoomAdmin } from '../../../redux/slices/Admin/adminSlice';
import { money } from '../adminUtils';
import AdminImagesField from '../AdminImagesField';

// Debe coincidir con ROOM_TYPES / MAX_ROOM_IMAGES en macarios-api/model/Room.js
const ROOM_TYPES = [
  { value: 'standard', label: 'Estándar' },
  { value: 'deluxe', label: 'Deluxe' },
  { value: 'suite', label: 'Suite' },
  { value: 'luxury', label: 'Lujo' },
];
const MAX_IMAGES = 3;

const EMPTY_FORM = {
  roomNumber: '',
  name: '',
  type: 'standard',
  basePricePerNight: '',
  maxDogs: '1',
  size: '',
  description: '',
  isActive: true,
};

const inputClass =
  'w-full p-2.5 border border-[#3A2A20]/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition text-sm';
const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60 mb-1.5';

const RoomsTab = ({ rooms, onDeleteRoom }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(EMPTY_FORM);
  const [keptImages, setKeptImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [editingId, setEditingId] = useState(null); // null = creando
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setKeptImages([]);
    setNewImages([]);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (room) => {
    setForm({
      roomNumber: room.roomNumber || '',
      name: room.name || '',
      type: room.type || 'standard',
      basePricePerNight: String(room.basePricePerNight ?? ''),
      maxDogs: String(room.maxDogs ?? 1),
      size: room.size === null || room.size === undefined ? '' : String(room.size),
      description: room.description || '',
      isActive: room.isActive !== false,
    });
    setKeptImages(room.images || []);
    setNewImages([]);
    setEditingId(room._id);
    setShowForm(true);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // FormData porque puede incluir fotos
    const roomData = new FormData();
    Object.entries(form).forEach(([key, value]) => roomData.append(key, String(value)));
    if (editingId) roomData.append('keepImages', JSON.stringify(keptImages));
    newImages.forEach((file) => roomData.append('images', file));

    const result = editingId
      ? await dispatch(updateRoomAdmin({ id: editingId, roomData }))
      : await dispatch(createRoomAdmin(roomData));

    setSaving(false);
    if (result.error) {
      setError(result.payload || 'No se pudo guardar el cuarto');
      return;
    }
    resetForm();
  };

  const handleToggleActive = (room) => {
    const roomData = new FormData();
    roomData.append('isActive', String(!room.isActive));
    dispatch(updateRoomAdmin({ id: room._id, roomData }));
  };

  return (
    <div>
      {!showForm && (
        <button
          onClick={handleNew}
          className="px-4 py-2.5 text-sm font-medium bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl mb-4 transition"
        >
          + Agregar cuarto
        </button>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-[#3A2A20]/10 rounded-2xl p-6 mb-6 space-y-4">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20]">
            {editingId ? 'Editar cuarto' : 'Nuevo cuarto'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nombre</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                maxLength={100}
                placeholder="Ej. Suite Jardín"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Número de cuarto</label>
              <input
                name="roomNumber"
                value={form.roomNumber}
                onChange={handleChange}
                required
                maxLength={20}
                placeholder="Ej. 101"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Tipo</label>
              <select name="type" value={form.type} onChange={handleChange} className={inputClass}>
                {ROOM_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Precio base por noche (MXN)</label>
              <input
                name="basePricePerNight"
                type="number"
                min="0"
                step="0.01"
                value={form.basePricePerNight}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Máximo de perros</label>
              <input
                name="maxDogs"
                type="number"
                min="1"
                max="10"
                step="1"
                value={form.maxDogs}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Tamaño (m²)</label>
              <input
                name="size"
                type="number"
                min="0"
                step="0.1"
                value={form.size}
                onChange={handleChange}
                placeholder="Opcional"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows="3"
              maxLength={1000}
              className={inputClass}
            />
          </div>

          <AdminImagesField
            keptImages={keptImages}
            setKeptImages={setKeptImages}
            newImages={newImages}
            setNewImages={setNewImages}
            max={MAX_IMAGES}
            onError={setError}
            labelClass={labelClass}
          />

          <label className="flex items-center gap-2 text-sm text-[#3A2A20]">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="w-4 h-4 accent-[#9A0002]"
            />
            Disponible para reservar
          </label>

          {error && <p className="text-[#9A0002] text-sm">{error}</p>}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 text-sm font-medium bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl transition disabled:opacity-50"
            >
              {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear cuarto'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2.5 text-sm font-medium border border-[#3A2A20]/20 text-[#3A2A20] rounded-xl hover:bg-[#3A2A20]/5 transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {rooms.length === 0 ? (
        <p className="text-sm text-[#3A2A20]/60">Aún no hay cuartos. Agrega el primero para empezar a recibir reservas.</p>
      ) : (
        <div className="grid gap-3">
          {rooms.map((r) => (
            <div
              key={r._id}
              className={`bg-white border border-[#3A2A20]/10 rounded-xl p-4 flex flex-wrap gap-4 justify-between items-center ${
                r.isActive === false ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-center gap-4 min-w-0">
                {r.images?.[0] ? (
                  <img src={r.images[0]} alt={r.name} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-[#EFE6DD] shrink-0" />
                )}
                <div className="min-w-0">
                  <h3 className="font-semibold text-[#3A2A20] truncate">
                    {r.name}
                    {r.isActive === false && (
                      <span className="ml-2 text-xs font-normal text-[#3A2A20]/50">(no disponible)</span>
                    )}
                  </h3>
                  <p className="text-sm text-[#3A2A20]/60">
                    Cuarto {r.roomNumber} · {ROOM_TYPES.find((t) => t.value === r.type)?.label || r.type} ·{' '}
                    {money(r.basePricePerNight)}/noche · hasta {r.maxDogs} perro{r.maxDogs === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleActive(r)}
                  className="px-3 py-1.5 text-xs font-medium bg-[#3A2A20]/5 text-[#3A2A20] rounded-lg hover:bg-[#3A2A20]/10 transition"
                >
                  {r.isActive === false ? 'Activar' : 'Desactivar'}
                </button>
                <button
                  onClick={() => handleEdit(r)}
                  className="px-3 py-1.5 text-xs font-medium bg-[#3A2A20]/5 text-[#3A2A20] rounded-lg hover:bg-[#3A2A20]/10 transition"
                >
                  Editar
                </button>
                <button
                  onClick={() => onDeleteRoom(r._id)}
                  className="px-3 py-1.5 text-xs font-medium bg-[#9A0002]/10 text-[#9A0002] rounded-lg hover:bg-[#9A0002]/20 transition"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoomsTab;
